import React, { Component } from 'react';
import { array, object, func, bool } from 'prop-types';
import take from 'lodash/take';
import sortBy from 'lodash/sortBy';
import sortByOrder from 'lodash/orderBy';
import flow from 'lodash/flow';
import partialRight from 'lodash/partialRight';
import { StyleSheet, css } from 'aphrodite';
import { Modal } from 'react-overlays';
import Form from '../form/Form';
import TextArea from '../form/formComponents/TextArea';
import SubmitButton from '../form/formComponents/SubmitButton';
import OccupationAutocomplete from './OccupationAutocomplete';
import MODES from '../form/onSubmitModes';
import i18n from '../../../shared/locales/no.json';

const sortAndLimitOccupations = flow(
    partialRight(sortByOrder, ['count', 'id'], ['desc', 'asc']),
    partialRight(take, 5),
    partialRight(sortBy, 'id')
);

const styles = StyleSheet.create({
    backdrop: {
        zIndex: 3,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        transform: 'translate3d(0, 0, 0)',
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
    },
    modal: {
        zIndex: 4,
        width: '100%',
        maxWidth: 480,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: '0 auto',
        overflow: 'scroll',
        '@media(min-width: 480px)': {
            top: 100,
            bottom: 'auto',
            maxHeight: '80%',
        },
    },
    modalHeight: {
        '@media(min-width: 480px)': {
            // aphrodite and React don't support CSS fallback values, hence this ugly mess
            maxHeight: 'calc(100vh - 140px)',
        },
    },
    header: {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: 0,
        paddingTop: '56.25%',
    },
    headerInner: {
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.35), transparent)',
        top: 0,
        right: 0,
    },
});

class EditImageModal extends Component {
    static propTypes = {
        image: object.isRequired,
        occupations: array.isRequired,
        occupationsFromActiveAds: array.isRequired,
        occupationSuggestions: array.isRequired,
        onSave: func,
        onClose: func,
        saving: bool,
        error: bool,
    };

    state = {
        occupations: this.props.occupations,
        prevOccupations: this.props.occupations,
    };

    static getDerivedStateFromProps(props, state) {
        if (props.occupations !== state.prevOccupations) {
            return {
                occupations: props.occupations,
                prevOccupations: props.occupations,
            };
        }

        return null;
    }

    isOccupationSelected(occupation) {
        return (
            this.state.occupations.find(({ id }) => id === occupation.id) !=
            null
        );
    }

    handleSubmit = ({ description }) => {
        const occupations = this.state.occupations.map(
            ({ general, specialization }) => ({ general, specialization })
        ); // Remove id, count, and other metadata
        this.props.onSave({
            description,
            occupations,
        });
    };

    handleToggleOccupation = (occupation) => {
        if (!this.props.saving) {
            this.setState((state) => {
                const index = state.occupations.findIndex(
                    ({ id }) => id === occupation.id
                );
                const occupations =
                    index === -1
                        ? state.occupations.concat(occupation)
                        : state.occupations
                              .slice(0, index)
                              .concat(state.occupations.slice(index + 1));
                return { occupations };
            });
        }
    };

    handleCloseClick = (event) => {
        event.preventDefault();
        this.props.onClose();
    };

    render() {
        // Do NOT combine the `modal` and `modalHeight` rules into one (i.e. css(modal, modalHeight))
        // as it will prune the fallback max-height value.
        const containerClassName = `u-bg-milk u-position-fixed ${css(
            styles.modal
        )} ${css(styles.modalHeight)}`;
        return (
            <Modal
                aria-label={i18n.editImage.contentLabel}
                backdropClassName={`u-position-fixed ${css(styles.backdrop)}`}
                onBackdropClick={this.props.onClose}
                show
            >
                <div className={containerClassName}>
                    <div
                        className={`u-position-relative ${css(styles.header)}`}
                        style={{
                            backgroundImage: `url(${this.props.image.image.urls.preview})`,
                        }}
                    >
                        <div
                            className={`u-position-absolute ${css(
                                styles.headerInner
                            )}`}
                        >
                            <button
                                aria-label={i18n.editImage.closeWindow}
                                className="button button--pill icon icon--cross close"
                                onClick={this.handleCloseClick}
                                type="button"
                            >
                                <span className="u-screen-reader-only">
                                    Lukk
                                </span>
                            </button>
                        </div>
                    </div>
                    <Form
                        className="panel form-std"
                        onValidSubmit={this.handleSubmit}
                    >
                        <TextArea
                            classes="input input--textarea"
                            disabled={this.props.saving}
                            id="image_description"
                            label={i18n.editImage.descriptionLabel}
                            name="description"
                            placeholder={i18n.editImage.descriptionPlaceholder}
                            rows="3"
                            validationErrorMsg={i18n.editImage.maxLength}
                            validations="maxLength:255"
                            value={this.props.image.description}
                        />
                        <h3 className="mtl mbn">
                            {i18n.editImage.jobTypeIntro}
                        </h3>
                        {Boolean(
                            this.props.occupationsFromActiveAds.length
                        ) && (
                            <fieldset className="form-std__fieldset">
                                <legend>
                                    {i18n.editImage.jobTypeSuggestions}
                                </legend>
                                {sortAndLimitOccupations(
                                    this.props.occupationsFromActiveAds
                                ).map((o) => (
                                    <div className="input-toggle" key={o.id}>
                                        <input
                                            checked={this.isOccupationSelected(
                                                o
                                            )}
                                            disabled={this.props.saving}
                                            id={o.id}
                                            onChange={this.handleToggleOccupation.bind(
                                                this,
                                                o
                                            )}
                                            type="checkbox"
                                        />
                                        <label htmlFor={o.id}>
                                            <span className="u-stone">
                                                {o.general} /{' '}
                                            </span>{' '}
                                            {o.specialization}
                                        </label>
                                    </div>
                                ))}
                            </fieldset>
                        )}
                        <div className="u-mt32">
                            <div className="input input--text">
                                <label>
                                    {this.props.occupationsFromActiveAds.length
                                        ? i18n.editImage.jobTypeLabel
                                        : i18n.editImage.jobTypeLabelAlt}
                                </label>
                                <OccupationAutocomplete
                                    countTemplate={i18n.editImage.jobTypeCount}
                                    disabled={this.props.saving}
                                    noResultsMessage={i18n.editImage.noHits}
                                    occupations={
                                        this.props.occupationSuggestions
                                    }
                                    onSelect={this.handleToggleOccupation}
                                    placeholder={
                                        i18n.editImage.jobTypePlaceholder
                                    }
                                />
                                {this.state.occupations.map((o) => (
                                    <button
                                        className="button button--pill button--has-icon tag"
                                        key={o.id}
                                        onClick={this.handleToggleOccupation.bind(
                                            this,
                                            o
                                        )}
                                        title={`${o.general}, ${o.specialization}`}
                                        type="button"
                                    >
                                        <span className="opacity75">
                                            {o.general} /{' '}
                                        </span>
                                        {o.specialization}
                                        <span className="icon icon--cross" />
                                    </button>
                                ))}
                            </div>
                        </div>
                        {this.props.error && (
                            <div className="u-bg-salmon">
                                <p>{i18n.editImage.oops}</p>
                            </div>
                        )}
                        <div className="u-text-center">
                            <SubmitButton
                                className="button--cta"
                                mode={
                                    this.props.saving
                                        ? MODES.SAVING
                                        : MODES.EDIT
                                }
                                value={i18n.editImage.save}
                            />
                        </div>
                    </Form>
                </div>
            </Modal>
        );
    }
}

export default EditImageModal;
